import { z } from "zod";
import { HOST } from "./host.js";
/**
 * MISSIONS — one frame, many games.
 *
 * Every mission, in the room or on phones, runs the same five beats:
 *   brief → private reveal → play → result → (pot)
 * and ends with a score from 0 to 100, which a tier turns into gold.
 * What happens inside "play" is drawn from a deck, so no two nights match.
 *
 * Phone missions are generators: each play builds a fresh puzzle from the
 * challenge bank and the seed. Room missions are the house-and-furniture
 * missions in the content pack; the Host scores them by tier.
 */
export const MISSION_MODES = ["physical", "phone", "mix"];
export const PLAY_KINDS = ["speed-lock", "closest-guess", "sort-it", "blackout", "potion-mixer", "seal-pairs", "portrait-gallery", "spot-the-change", "link-up"];
// ---------- Scoring ----------
export const TIERS = ["flawless", "strong", "patchy", "shaky", "failure"];
export const TIER_INFO = {
    flawless: { label: "Flawless", min: 90, factor: 1.25, score: 95 },
    strong: { label: "Strong", min: 70, factor: 0.75, score: 80 },
    patchy: { label: "Patchy", min: 40, factor: 0.4, score: 55 },
    shaky: { label: "Shaky", min: 10, factor: 0.15, score: 25 },
    failure: { label: "Failure", min: 0, factor: 0, score: 0 },
};
export function tierFor(score) {
    return TIERS.find((t) => score >= TIER_INFO[t].min) ?? "failure";
}
/** Gold for a score: the tier's share of the mission's value, rounded down to 50. */
export function goldFor(potValue, score) {
    return Math.floor((potValue * TIER_INFO[tierFor(score)].factor) / 50) * 50;
}
/** Mission values in the content are multiplied by this: big numbers, as on the show. */
export const POT_SCALE = 8;
/** The most the pot can ever hold. */
export const MAX_POT = 100_000;
/** Room-mission sabotage takes a quarter off, never more: subtle damage is deniable. */
export const SABOTAGE_SHARE = 0.25;
// ---------- Twists ----------
export const TWISTS = ["rush", "silent", "decoy", "fog"];
export const TWIST_INFO = {
    rush: { badge: "Rush", line: "Half the time. Half as much again for the pot.", kinds: PLAY_KINDS },
    silent: { badge: "Silent", line: "Not a word. Point, nod and gesture only.", kinds: ["speed-lock", "sort-it", "potion-mixer", "seal-pairs", "portrait-gallery", "spot-the-change", "link-up"] },
    decoy: { badge: "Decoy", line: "One clue tonight is a lie.", kinds: ["speed-lock"] },
    fog: { badge: "Fog", line: "Your clue fades after a few seconds. Remember it.", kinds: ["speed-lock"] },
};
export const PLAY_INFO = {
    "speed-lock": {
        title: "Speed Lock",
        goal: "Open the lock. Every digit you get right pays.",
        how: ["Solve the puzzle on your phone", "Your answer reveals one digit", "Call it out. The Keyholder types the code"],
        skins: [
            { id: "vault", hook: "The Duchess's vault holds tonight's gold. One code stands between you and it." },
            { id: "hatch", hook: "The submarine hatch is sealing. The code to open it is scattered among you." },
            { id: "tomb", hook: "The tomb door has not opened in three thousand years. Tonight it might." },
            { id: "museum", hook: "The museum alarm resets in moments. The case code is in your hands." },
            { id: "train", hook: "The night train's strongbox is locked. The guard split the code between you." },
        ],
        playSec: 150,
        reveal: true,
        potValue: 2500,
    },
    "closest-guess": {
        title: "Closest Guess",
        goal: "Three questions with a number for an answer. The closer the table, the more gold.",
        how: ["Talk it through", "Everyone types a number", "The middle guess is the table's answer"],
        skins: [
            { id: "auction", hook: "The auctioneer wants a figure. Guess well and the room pays out." },
            { id: "cartographer", hook: "The castle's old cartographer kept count of everything. Can you?" },
            { id: "bet", hook: "The house is taking bets. The table that guesses closest takes the pot." },
        ],
        playSec: 40,
        reveal: false,
        potValue: 2000,
    },
    "sort-it": {
        title: "Sort It",
        goal: "Sixteen tiles hide four groups of four. Put every tile in its group.",
        how: ["Your phone holds some of the tiles", "Agree the groups out loud", "Give each of your tiles a colour"],
        skins: [
            { id: "library", hook: "The library's shelves have been ransacked. Put everything back where it belongs." },
            { id: "evidence", hook: "The evidence board is a mess. Somebody wants it that way." },
            { id: "wardrobe", hook: "The wardrobe mistress has mixed up four costumes. Sort them before curtain up." },
        ],
        playSec: 150,
        reveal: true,
        potValue: 2500,
    },
    blackout: {
        title: "Blackout",
        goal: "Three scenes. Study each one, then agree one answer as a table: the most popular answer is the one that counts.",
        how: ["Watch the TV closely", "The lights go out", "Agree out loud, then all pick the same answer"],
        skins: [
            { id: "witness", hook: "Every one of you is a witness tonight. What did you see?" },
            { id: "gallery", hook: "The gallery lights will fail any moment. Look while you can." },
            { id: "seance", hook: "The candles gutter. Remember everything before the dark." },
        ],
        playSec: 25,
        reveal: false,
        potValue: 2000,
    },
    "potion-mixer": {
        title: "Potion Mixer",
        goal: "Brew a potion the exact colour of the recipe. The closer the colour, the more gold.",
        how: ["You pour one ingredient", "Your phone holds the recipe for someone else's: tell them", "Pour, watch the flask, seal it together"],
        skins: [
            { id: "apothecary", hook: "The castle apothecary left one recipe behind. Brew it before the candle burns down." },
            { id: "alchemist", hook: "The alchemist's last potion must be matched, drop for drop." },
            { id: "witch", hook: "Something is bubbling in the cellar. Make it the right colour, or else." },
        ],
        playSec: 120,
        reveal: true,
        potValue: 2500,
    },
    "portrait-gallery": {
        title: "Portrait Gallery",
        goal: "Three drawings, three artists. The more of you who guess each one, the more gold.",
        how: ["The artist draws on their phone", "The picture appears on the TV", "Everyone else types their guesses"],
        skins: [
            { id: "gallery", hook: "The castle's portrait gallery has three empty frames. Fill them." },
            { id: "sketchbook", hook: "A sketchbook was found in the turret. The pages are still blank." },
            { id: "commission", hook: `${HOST.name} has commissioned three portraits. The artists are among you.` },
        ],
        playSec: 60,
        reveal: false,
        potValue: 2500,
    },
    "spot-the-change": {
        title: "Spot the Change",
        goal: "Study the room. When it comes back, five things are different. Circle every one.",
        how: ["Study the room on the TV", "The room changes", "Circle the changes on your phone, together"],
        skins: [
            { id: "study", hook: `Someone has been in ${HOST.the}'s study. Nothing is quite where it was.` },
            { id: "burglar", hook: "A thief crept through the great hall last night. What did they touch?" },
            { id: "poltergeist", hook: "The castle ghost likes to rearrange things. Catch it in the act." },
        ],
        playSec: 60,
        reveal: true,
        revealSec: 15,
        potValue: 2500,
    },
    "link-up": {
        title: "Link-up",
        goal: "Join every pair of matching lanterns. Fill every square of the courtyard for the full pot.",
        how: ["Your phone holds one or two colours", "Drag a path between your two lanterns", "Paths can't cross. Share the space"],
        skins: [
            { id: "lanterns", hook: "The courtyard lanterns must be lit before dark. Every one has a twin." },
            { id: "ropes", hook: "The castle bells need ringing. Run each rope to its bell without a tangle." },
            { id: "garden", hook: `${HOST.name} wants new hedges in the maze. Every gate needs its path.` },
        ],
        playSec: 120,
        reveal: true,
        potValue: 2500,
    },
    "seal-pairs": {
        title: "Seal Pairs",
        goal: "Sixteen wax seals hide eight pairs. Find them all before time runs out.",
        how: ["Take turns on your phone", "Flip two seals", "Shout out what you remember"],
        skins: [
            { id: "letters", hook: "The steward's letters have been resealed in a hurry. Match every seal." },
            { id: "vault", hook: "The vault's seals come in pairs. Break the wrong one and it closes again." },
            { id: "archive", hook: "The archive is full of sealed scrolls. Every one has a twin." },
        ],
        playSec: 120,
        reveal: false,
        potValue: 2500,
    },
};
/** Portrait Gallery: things anyone can draw in a minute. Family-safe, British English. */
export const PORTRAIT_WORDS = [
    "castle", "crown", "dragon", "candle", "key", "ghost", "sword", "bridge", "horse", "owl", "moon", "ship",
    "lighthouse", "tree", "snowman", "umbrella", "guitar", "bicycle", "teapot", "cake", "pizza", "banana",
    "rainbow", "volcano", "spider", "snail", "rocket", "robot", "clock", "book", "glasses", "hat", "sock",
    "whale", "octopus", "butterfly", "cactus", "mushroom", "carrot", "ladder", "kite", "balloon", "anchor",
    "tent", "train", "tractor", "aeroplane", "helicopter", "crab", "penguin", "giraffe", "elephant", "snake",
    "frog", "duck", "pig", "sheep", "rabbit", "bee", "ladybird", "tortoise", "crocodile", "pirate", "king",
    "knight", "witch", "wizard", "cauldron", "broom", "mirror", "fountain", "windmill", "pyramid", "island",
    "mountain", "waterfall", "igloo", "scarecrow", "skeleton", "diamond", "treasure", "map", "compass",
    "telescope", "camera", "television", "toothbrush", "scissors", "hammer", "bucket", "sandcastle", "lollipop",
    "doughnut", "hedgehog", "unicorn", "mermaid", "parrot", "spaceship", "trophy", "fireworks", "jellyfish",
];
/** Portrait Gallery ink, chosen to read well on parchment. */
export const PORTRAIT_INKS = ["#1c1a17", "#b3203a", "#2f6fd6", "#3f8f3a", "#c98a12", "#7a4a2a"];
/** Drawings travel as points on a 1000 × 750 canvas. */
export const PORTRAIT_CANVAS = { width: 1000, height: 750, maxPoints: 240, maxStrokes: 160 };
/** Is this guess the word? Forgiving of case, spaces, a leading "a/the" and a plural "s". */
export function guessMatches(guess, word) {
    const n = (s) => s
        .toLowerCase()
        .replace(/[^a-z ]/g, "")
        .trim()
        .replace(/^(a|an|the) /, "")
        .replace(/\s+/g, "");
    const g = n(guess);
    const w = n(word);
    return g === w || g === `${w}s` || `${g}s` === w || g === `${w}es`;
}
/** Potion Mixer's ingredients: a colour and a name each. */
export const POTION_INGREDIENTS = [
    { name: "Dragon's blood", colour: "#c0182b" },
    { name: "Sun gold", colour: "#f2b705" },
    { name: "Moonwater", colour: "#2f6fd6" },
    { name: "Bone white", colour: "#f4efe6" },
    { name: "Nightshade", colour: "#3a1f5c" },
    { name: "Moss", colour: "#3f8f3a" },
];
/** Most drops one phone can pour of its ingredient. */
export const POTION_MAX_DROPS = 10;
const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
/** Mix ingredients by drops: a weighted blend of their colours. Nothing in the flask is grey. */
export function mixPotion(colours, drops) {
    const total = drops.reduce((a, b) => a + b, 0);
    if (total <= 0)
        return "#3b3f3e";
    const rgb = [0, 0, 0];
    colours.forEach((c, i) => hexToRgb(c).forEach((v, ch) => (rgb[ch] = (rgb[ch] ?? 0) + (v * (drops[i] ?? 0)) / total)));
    return `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
}
/** How alike two colours are, as a 0–100 score. */
export function potionScore(a, b) {
    const x = hexToRgb(a);
    const y = hexToRgb(b);
    const d = Math.sqrt(x.reduce((s, v, i) => s + (v - y[i]) ** 2, 0));
    return Math.round(Math.max(0, Math.min(100, 100 * (1 - (d - 10) / 90))));
}
/** Link-up: each pair's colour, and a shape so nobody needs colour vision. */
export const LINK_COLOURS = [
    { name: "Crimson", colour: "#d1344f", shape: "●" },
    { name: "Gold", colour: "#e6b32e", shape: "▲" },
    { name: "Sky", colour: "#4f9be0", shape: "■" },
    { name: "Ivy", colour: "#4fae6a", shape: "◆" },
    { name: "Violet", colour: "#9a6ad6", shape: "★" },
    { name: "Ember", colour: "#e57a2e", shape: "✚" },
    { name: "Pearl", colour: "#e9e3d3", shape: "⬟" },
];
/** Seal Pairs: the symbols pressed into the wax. */
export const SEAL_SYMBOLS = ["🦉", "🦊", "🦌", "🐦‍⬛", "🐺", "🐇", "🐻", "🦋", "🐗", "🐍", "🦅", "🦡"];
export const SEAL_COUNT = 16;
/** A player who hasn't flipped in this long loses the turn to whoever flips first. */
export const SEAL_TURN_MS = 12_000;
export const PLAY_TIMING = {
    briefMs: 15_000,
    revealMs: 6_000,
    /** Between steps of a multi-step mission (Closest Guess, Blackout). */
    stepRevealMs: 7_000,
    resultMs: 9_000,
};
/** Sort It's four groups: a colour and a shape each, so nobody needs colour vision. */
export const SORT_GROUPS = [
    { name: "Gold", shape: "●", colour: "#d9b45a" },
    { name: "Crimson", shape: "▲", colour: "#c4364c" },
    { name: "Moon", shape: "■", colour: "#8fb3d6" },
    { name: "Ivy", shape: "◆", colour: "#5fa67a" },
];
// ---------- Player input ----------
export const PlayInput = z.discriminatedUnion("k", [
    /** Speed Lock puzzle or Blackout question. */
    z.object({ k: z.literal("answer"), option: z.number().int().min(0).max(5).nullable().default(null), text: z.string().max(60).nullable().default(null) }),
    /** Speed Lock Keyholder: the code typed so far. */
    z.object({ k: z.literal("code"), code: z.string().regex(/^[0-9]{0,6}$/) }),
    /** Speed Lock Keyholder: try the code now. */
    z.object({ k: z.literal("unlock") }),
    /** Closest Guess. */
    z.object({ k: z.literal("guess"), value: z.number().finite().min(-1e9).max(1e9) }),
    /** Sort It: put one of your tiles in a group (null to clear). */
    z.object({ k: z.literal("tile"), tile: z.number().int().min(0).max(15), group: z.number().int().min(0).max(3).nullable() }),
    /** Potion Mixer: how many drops of your ingredient are in. */
    z.object({ k: z.literal("drops"), value: z.number().int().min(0).max(10) }),
    /** Portrait Gallery: the artist adds a stroke, or wipes the page. */
    z.object({
        k: z.literal("stroke"),
        points: z.array(z.number().int().min(0).max(1000)).min(2).max(480),
        ink: z.number().int().min(0).max(5),
    }),
    z.object({ k: z.literal("wipe") }),
    /** Portrait Gallery: a guess at the drawing. */
    z.object({ k: z.literal("word"), text: z.string().min(1).max(40) }),
    /** Spot the Change: circle a place in the room, or rub the circle out. */
    z.object({ k: z.literal("mark"), slot: z.number().int().min(0).max(31) }),
    /** Link-up: the whole path for one colour, from one of its lanterns (empty to clear it). */
    z.object({ k: z.literal("path"), colour: z.number().int().min(0).max(6), cells: z.array(z.number().int().min(0).max(80)).max(81) }),
    /** Seal Pairs: flip a seal. */
    z.object({ k: z.literal("flip"), cell: z.number().int().min(0).max(15) }),
    /** "Ready": skip the brief, or "we're done" in Sort It. */
    z.object({ k: z.literal("ready") }),
]);
// ---------- Link-up helpers (shared by the core and the phones) ----------
/** Are two cells side by side on a size × size grid? */
export function linkAdjacent(a, b, size) {
    const [ax, ay, bx, by] = [a % size, Math.floor(a / size), b % size, Math.floor(b / size)];
    return Math.abs(ax - bx) + Math.abs(ay - by) === 1;
}
/** Does this path join its two lanterns? */
export function linkJoined(path, ends) {
    if (path.length < 2)
        return false;
    const a = path[0];
    const b = path[path.length - 1];
    return (a === ends[0] && b === ends[1]) || (a === ends[1] && b === ends[0]);
}
/**
 * Why a path can't be drawn, or null if it can. A path starts at one of its
 * own lanterns, steps between neighbouring squares, never doubles back on
 * itself, never runs over another colour's lantern, and stops at its twin.
 */
export function linkPathProblem(cells, colour, ends, size) {
    if (cells.length === 0)
        return null;
    const mine = ends[colour];
    if (!mine)
        return "No such colour.";
    if (cells[0] !== mine[0] && cells[0] !== mine[1])
        return "Start from one of your lanterns.";
    const seen = new Set();
    const others = new Set(ends.flatMap((e, i) => (i === colour ? [] : [e[0], e[1]])));
    for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        if (c < 0 || c >= size * size)
            return "That's off the courtyard.";
        if (seen.has(c))
            return "A path can't cross itself.";
        if (others.has(c))
            return "That lantern belongs to another colour.";
        if (i > 0 && !linkAdjacent(cells[i - 1], c, size))
            return "Paths go square by square.";
        if (i > 0 && i < cells.length - 1 && (c === mine[0] || c === mine[1]))
            return "The path ends at the lantern.";
        seen.add(c);
    }
    return null;
}
//# sourceMappingURL=missions.js.map